import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card, Badge, Select, Alert, EmptyState } from '../components/design-system';
import { Users, Shield, ShieldCheck, ShieldAlert, UserX } from 'lucide-react';

const roleOptions = [
  { value: 'superadmin', label: 'Superadmin' },
  { value: 'admin', label: 'Admin' },
  { value: 'editor', label: 'Editor' },
  { value: 'user', label: 'User' },
];

const roleBadgeVariant = { superadmin: 'danger', admin: 'warning', editor: 'info', user: 'default' };
const roleIcons = { superadmin: ShieldAlert, admin: ShieldCheck, editor: Shield, user: Users };

export const UsersPage = () => {
  const { isSuperAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const res = await api.getUsers();
      setUsers(res.users || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleRoleChange = async (userId, newRole) => {
    setError(''); setSuccess('');
    try {
      await api.updateUser(userId, { role: newRole });
      setSuccess('Role updated');
      loadUsers();
    } catch (err) { setError(err.message); }
  };

  const handleDeactivate = async (userId) => {
    if (!confirm('Deactivate this user? They will no longer be able to access the app.')) return;
    setError('');
    try {
      await api.deactivateUser(userId);
      setSuccess('User deactivated');
      loadUsers();
    } catch (err) { setError(err.message); }
  };

  return (
    <div className="space-y-4 animate-fade-in max-w-4xl">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Users & Roles</h2>
        <p className="text-sm text-gray-500">Manage access and permissions for newsdesk users</p>
      </div>

      {error && <Alert type="error" onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" /></div>
      ) : users.length === 0 ? (
        <EmptyState icon={Users} title="No users" description="Users appear here when they log in to the app." />
      ) : (
        <div className="space-y-3">
          {users.map(u => {
            const RoleIcon = roleIcons[u.role] || Users;
            const isCurrentUser = u.account_user_id === currentUser?.id;
            return (
              <Card key={u.id} className="!p-4">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-medium text-primary-700">
                      {(u.display_name?.[0] || u.email?.[0] || '?').toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{u.display_name || 'Unnamed'}</span>
                      {isCurrentUser && <Badge variant="primary">You</Badge>}
                      {!u.is_active && <Badge variant="danger">Inactive</Badge>}
                    </div>
                    <div className="text-xs text-gray-500">{u.email}</div>
                    <div className="text-xs text-gray-400 mt-0.5">Joined {new Date(u.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    {isSuperAdmin && !isCurrentUser ? (
                      <Select
                        options={roleOptions}
                        value={u.role}
                        onChange={(e) => handleRoleChange(u.id, e.target.value)}
                        className="w-32"
                      />
                    ) : (
                      <Badge variant={roleBadgeVariant[u.role]} className="capitalize">{u.role}</Badge>
                    )}
                    {isSuperAdmin && !isCurrentUser && u.is_active && (
                      <Button variant="ghost" size="sm" icon={UserX} onClick={() => handleDeactivate(u.id)} />
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Card className="!bg-gray-50">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Role Permissions</h4>
        <div className="space-y-1 text-xs text-gray-500">
          <div><Badge variant="danger" className="mr-2">Superadmin</Badge>Full access, manage users & roles, system settings</div>
          <div><Badge variant="warning" className="mr-2">Admin</Badge>Manage sources, articles, API keys, settings</div>
          <div><Badge variant="info" className="mr-2">Editor</Badge>Add/edit sources, manage articles, trigger fetches</div>
          <div><Badge className="mr-2">User</Badge>View dashboard, feed, and articles (read-only)</div>
        </div>
      </Card>
    </div>
  );
};
